import * as Phaser from 'phaser';
import { Hud, type HudCallbacks, type HudState } from '../ui/hud';
import { LabAudio } from '../core/audio';
import { createLocalAnalytics, type OddLabAnalytics } from '../core/analytics';
import { loadSave, writeSave, type GameSave, type PlacedToolSave, addDiscovery, encodeSceneCode, decodeSceneCode } from '../core/storage';
import {
  TOOLS,
  dailyChallenge,
  type ToolDef,
  type RuleId,
  type GameMode,
} from '../core/catalog';
import {
  getRule,
  measureDistance,
  evaluateRun,
  shouldMarkSecondExperiment,
  detectVariableChange,
  toolSignature,
  TOOL_PHYSICS,
  clamp,
  worldFloorY,
} from './physicsRules';
import { generateTextures, drawToolGraphics } from './textures';
import { uid, todayKey, dailySeed } from '../core/constants';

interface PlacedTool {
  id: string;
  toolId: string;
  sprite: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite;
  angle: number;
  x: number;
  y: number;
}

export class OddLabScene extends Phaser.Scene {
  private hud!: Hud;
  private audio!: LabAudio;
  private analytics!: OddLabAnalytics;
  private save!: GameSave;

  private mode: GameMode = 'quick';
  private currentRule: RuleId = 'normal';
  private isRunning = false;
  private isPaused = false;
  private selectedToolId: string | null = null;
  private selectedPlaced: PlacedTool | null = null;
  private placedTools: PlacedTool[] = [];
  private bot?: Phaser.Physics.Arcade.Sprite;
  private botTag?: Phaser.GameObjects.Text;
  private floor?: any;
  private goalX = 520;
  private originX = 120;
  private botStartX = 120;
  private botStartY = 316;
  private runStartTime = 0;
  private lastRunDistance = 0;
  private bestDistance = 0;
  private runCount = 0;
  private hasSecondStarted = false;
  private debug = false;
  private lastToolSignature = '';
  private simTimer?: Phaser.Time.TimerEvent;
  private particles?: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor() {
    super({ key: 'OddLabScene' });
  }

  create() {
    const urlParams = new URLSearchParams(window.location.search);
    this.debug = urlParams.get('debug') === '1' || urlParams.get('debug') === 'true';

    this.save = loadSave();
    this.mode = (this.save.lastMode as GameMode) || 'quick';
    this.currentRule = this.save.freeLab?.rule || 'normal';
    this.bestDistance = this.save.quick?.bestDistance || 0;
    this.runCount = this.save.quick?.runCount || 0;

    this.audio = new LabAudio();
    this.analytics = createLocalAnalytics();
    (this.analytics as any).setMode?.(this.mode);
    (this.analytics as any).setRule?.(this.currentRule);
    (this.analytics as any).markLoadStart?.();

    const W = 800;
    const H = 480;
    generateTextures(this);
    this.cameras.main.setBackgroundColor('#171322');
    this.physics.world.setBounds(0, 0, W, H + 20);
    const ruleDef = getRule(this.currentRule);
    this.physics.world.gravity.y = ruleDef.gravityY * 980;

    this.drawLabWorld();
    this.floor = this.physics.add.staticImage(W / 2, worldFloorY(H), 'tex_floor')
      .setDisplaySize(W, 90)
      .refreshBody();

    this.drawGoalLine();
    this.createBot();

    this.particles = this.add.particles(0, 0, 'tex_spark', {
      speed: { min: 20, max: 90 },
      scale: { start: 0.7, end: 0 },
      lifespan: 300,
      quantity: 2,
      emitting: false,
    } as any);

    this.createHud();
    this.resetScene(true);

    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);

    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-ESC', () => this.clearSelection());
      this.input.keyboard.on('keydown-R', () => this.onRun());
      this.input.keyboard.on('keydown-SPACE', () => this.onRun());
    }

    (window as any).addEventListener('oddlab-tray-drag', (e: any) => {
      const d = e.detail;
      if (d?.toolId) {
        this.selectedToolId = d.toolId;
        this.updateHudState();
      }
    });

    (this.analytics as any).markPlayable?.();
    this.analytics.track('game_page_view');

    if (this.mode === 'free' && this.save.freeLab?.tools?.length) {
      this.restorePlacedTools(this.save.freeLab.tools);
    }

    const splash = document.getElementById('boot-splash');
    if (splash) setTimeout(() => splash.classList.add('hide'), 120);

    if (this.debug) {
      console.log('[OddLab] debug=1, use window.__oddLabAnalytics');
    }
  }

  private drawLabWorld() {
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x2b2340, 0x2b2340, 0x171322, 0x171322, 1);
    bg.fillRect(0, 0, 800, 480);

    bg.lineStyle(1, 0x6c5b8a, 0.17);
    for (let x = 0; x <= 800; x += 40) bg.lineBetween(x, 0, x, 390);
    for (let y = 0; y <= 390; y += 40) bg.lineBetween(0, y, 800, y);

    bg.fillStyle(0x120f1d, 0.72);
    bg.fillRoundedRect(20, 105, 120, 210, 22);
    bg.fillRoundedRect(655, 90, 120, 245, 22);
    bg.lineStyle(8, 0x4d4564, 1);
    bg.lineBetween(40, 120, 40, 330);
    bg.lineBetween(760, 110, 760, 340);
    bg.lineStyle(5, 0x55e5ff, 0.45);
    bg.strokeCircle(82, 170, 38);
    bg.strokeCircle(714, 165, 34);
    bg.fillStyle(0x55e5ff, 0.08);
    bg.fillCircle(82, 170, 34);
    bg.fillCircle(714, 165, 30);

    bg.fillStyle(0xffd84a, 0.9);
    for (let x = 0; x < 800; x += 40) bg.fillRect(x, 365, 20, 8);
    bg.fillStyle(0xff5f87, 0.16);
    bg.fillCircle(520, 236, 80);

    this.add.text(400, 98, 'TEST CHAMBER  /  THROW BUDDY PAST THE GATE', {
      fontFamily: 'Arial, sans-serif', fontSize: '12px', fontStyle: 'bold', color: '#8d80a8',
      letterSpacing: 2,
    }).setOrigin(0.5);
  }

  private drawGoalLine() {
    const g = this.add.graphics();
    g.fillStyle(0xff5f87, 0.10);
    g.fillRoundedRect(this.goalX - 22, 130, 44, 255, 20);
    g.lineStyle(8, 0xff5f87, 0.32);
    g.strokeRoundedRect(this.goalX - 22, 130, 44, 255, 20);
    g.lineStyle(4, 0xffd84a, 1);
    g.strokeLineShape(new Phaser.Geom.Line(this.goalX, 132, this.goalX, 386));
    g.fillStyle(0xffd84a, 1);
    g.fillTriangle(this.goalX - 15, 155, this.goalX - 15, 185, this.goalX + 15, 170);
    const label = this.add.text(this.goalX, 119, 'FINISH!', {
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#ffd84a',
      stroke: '#171322',
      strokeThickness: 5,
      fontFamily: 'Arial Rounded MT Bold, system-ui, sans-serif',
    });
    label.setOrigin(0.5);
  }

  private createBot(x = this.botStartX, y = this.botStartY) {
    if (this.bot) this.bot.destroy();
    if (this.botTag) this.botTag.destroy();
    this.bot = this.physics.add.sprite(x, y, 'tex_bot');
    this.bot.setCollideWorldBounds(true);
    this.bot.setBounce(0.35, 0.3);
    this.bot.setDrag(0.1, 0.03);
    this.bot.setMaxVelocity(480, 580);
    this.bot.setSize(46, 48).setOffset(12, 17);
    this.botTag = this.add.text(x, y - 52, 'BUDDY', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#171322',
      strokeThickness: 4,
    }).setOrigin(0.5);

    if (this.floor) {
      this.physics.add.collider(this.bot, this.floor, () => {
        if (this.isRunning && !this.isPaused) this.audio.bounce();
      });
    }
  }

  private createHud() {
    const container = document.getElementById('game-container') || document.body;
    const initial: HudState = {
      mode: this.mode,
      rule: this.currentRule,
      selectedTool: null,
      unlockedTools: this.save.unlockedTools || ['spring', 'fan', 'ramp'],
      running: false,
      paused: false,
      muted: !!(this.save.settings && this.save.settings.muted),
      goalText: 'Launch the bot past the red line',
      statusText: 'Tweak the toys. Make BUDDY fly.',
      canUndo: false,
      discoveries: this.save.discoveries || [],
      challengeProgress: {},
      debug: this.debug,
      showGhostHint: true,
    };

    const cbs: HudCallbacks = {
      onRun: () => this.onRun(),
      onPause: () => this.onPause(),
      onReset: () => this.resetScene(),
      onUndo: () => this.undoLast(),
      onMode: (m: GameMode) => this.switchMode(m),
      onSelectTool: (id: string | null) => this.selectTool(id),
      onRule: (r: RuleId) => this.changeRule(r),
      onChallenge: (c: any) => this.loadChallenge(c),
      onDaily: () => this.loadDaily(),
      onDeleteSelected: () => this.deleteSelected(),
      onRotateSelected: () => this.rotateSelected(),
      onToggleMute: () => this.toggleMute(),
      onShare: () => this.shareResult(),
      onImportCode: (code: string) => this.importCode(code),
      onSimulateAd: () => this.simAd(),
      onCloseResult: () => { this.hud.hideToast(); },
      onPlayReplay: () => this.playLastReplay(),
    };

    this.hud = new Hud(container, initial, cbs);
    if (this.debug) {
      this.hud.setDebugText('DEBUG: window.__oddLabAnalytics.getEvents()');
    }
    this.updateHudState();
  }

  private updateHudState(extra: Partial<HudState> = {}) {
    const s: Partial<HudState> = {
      mode: this.mode,
      rule: this.currentRule,
      selectedTool: this.selectedToolId,
      unlockedTools: this.save.unlockedTools || [],
      running: this.isRunning,
      paused: this.isPaused,
      muted: this.audio.isMuted(),
      goalText: this.mode === 'quick' ? 'Launch the bot past the red line' : 'Free Lab — experiment freely',
      statusText: this.getStatusText(),
      canUndo: this.placedTools.length > 0,
      discoveries: this.save.discoveries || [],
      debug: this.debug,
      showGhostHint: !this.isRunning && this.placedTools.length < 1 && this.mode === 'quick',
      ...extra,
    };
    this.hud.update(s);
  }

  private getStatusText(): string {
    if (this.isRunning) return this.isPaused ? 'BUDDY is frozen!' : 'BUDDY is flying!';
    if (this.runCount > 0) return `Landed ${Math.round(this.lastRunDistance)}m away — tweak and launch again!`;
    return 'Drag a toy. Then smash LAUNCH!';
  }

  private selectTool(toolId: string | null) {
    if (!toolId) {
      this.selectedToolId = null;
      this.updateHudState();
      return;
    }
    const def = TOOLS[toolId];
    if (!def) return;
    const unlocked = this.isToolUnlocked(toolId);
    if (this.mode === 'quick' && !unlocked && ['spring', 'fan', 'ramp'].indexOf(toolId) === -1) {
      this.hud.showResult({ title: 'Tool locked', body: 'Finish first experiment to unlock.' });
      return;
    }
    this.selectedToolId = toolId;
    this.clearSelection();
    this.updateHudState();
    this.analytics.track('object_placed', { tool_id: toolId });
  }

  private isToolUnlocked(id: string): boolean {
    const list = this.save.unlockedTools || [];
    return list.indexOf(id) !== -1 || ['spring', 'fan', 'ramp'].indexOf(id) !== -1;
  }

  private changeRule(rule: RuleId) {
    if (this.isRunning) return;
    const prev = this.currentRule;
    this.currentRule = rule;
    const r = getRule(rule);
    this.physics.world.gravity.y = r.gravityY * 980;
    (this.analytics as any).setRule?.(rule);
    this.analytics.track('rule_changed', { active_rule: rule });
    this.updateHudState();
    if (this.mode === 'free' || prev !== rule) {
      this.save.freeLab.rule = rule;
      writeSave(this.save);
    }
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer) {
    if (this.isRunning && !this.isPaused) return;

    const hit = this.hitTestPlaced(pointer.x, pointer.y);
    if (hit) {
      this.selectedPlaced = hit;
      this.selectedToolId = null;
      this.updateHudState();
      (hit.sprite as any)._dragOff = { x: pointer.x - hit.x, y: pointer.y - hit.y };
      return;
    }

    if (this.selectedToolId) {
      const def = TOOLS[this.selectedToolId];
      if (def) {
        this.placeTool(this.selectedToolId, pointer.x, pointer.y, 0);
        this.selectedToolId = null;
        this.updateHudState();
        this.saveIfFree();
      }
    }
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.selectedPlaced || this.isRunning) return;
    const pt = this.selectedPlaced;
    const off = (pt.sprite as any)._dragOff || { x: 0, y: 0 };
    pt.x = clamp(pointer.x - off.x, 70, 730);
    pt.y = clamp(pointer.y - off.y, 90, worldFloorY(480) - 15);
    pt.sprite.setPosition(pt.x, pt.y);
  }

  private handlePointerUp() {
    if (this.selectedPlaced) {
      this.saveIfFree();
      this.selectedPlaced = null;
      this.updateHudState();
    }
  }

  private hitTestPlaced(x: number, y: number): PlacedTool | null {
    for (let i = 0; i < this.placedTools.length; i++) {
      const pt = this.placedTools[i];
      const dx = Math.abs(x - pt.x);
      const dy = Math.abs(y - pt.y);
      const hw = (pt.toolId === 'ramp' ? 55 : 30);
      const hh = 25;
      if (dx < hw && dy < hh) return pt;
    }
    return null;
  }

  private placeTool(toolId: string, x: number, y: number, angle = 0) {
    const def = TOOLS[toolId];
    if (!def) return;
    const wx = clamp(x, 70, 730);
    const wy = clamp(y, 95, worldFloorY(480) - 12);

    const gfx = drawToolGraphics(this, toolId, def.color, def.w, def.h, def.shape);
    gfx.setPosition(wx, wy);
    gfx.setAngle(angle);
    const sprite = gfx as Phaser.GameObjects.Graphics;

    const pt: PlacedTool = {
      id: uid('t_'),
      toolId,
      sprite,
      angle,
      x: wx,
      y: wy,
    };
    this.placedTools.push(pt);

    sprite.setInteractive();
    sprite.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.isRunning) {
        this.selectedPlaced = pt;
        this.selectedToolId = null;
        (sprite as any)._dragOff = { x: p.x - wx, y: p.y - wy };
        this.updateHudState();
      }
    });

    this.updateHudState();
    this.audio.place();
  }

  private clearSelection() {
    this.selectedPlaced = null;
    this.selectedToolId = null;
    this.updateHudState();
  }

  private deleteSelected() {
    if (!this.selectedPlaced) return;
    const idx = this.placedTools.indexOf(this.selectedPlaced);
    if (idx >= 0) {
      this.placedTools[idx].sprite.destroy();
      this.placedTools.splice(idx, 1);
    }
    this.selectedPlaced = null;
    this.saveIfFree();
    this.updateHudState();
  }

  private rotateSelected() {
    if (!this.selectedPlaced) return;
    const pt = this.selectedPlaced;
    pt.angle = (pt.angle + 30) % 360;
    pt.sprite.setAngle(pt.angle);
    this.saveIfFree();
    this.updateHudState();
  }

  private applyToolForces() {
    if (!this.bot || !this.isRunning || this.isPaused) return;
    const bot = this.bot;
    const rule = getRule(this.currentRule);

    const wind = rule.windX * 380;
    if (Math.abs(wind) > 0.0001 && bot.body) {
      bot.setVelocityX(bot.body.velocity.x + wind * 0.6);
    }

    for (let i = 0; i < this.placedTools.length; i++) {
      const pt = this.placedTools[i];
      const dx = bot.x - pt.x;
      const dy = bot.y - pt.y;
      const distSq = dx * dx + dy * dy;
      const dist = Math.sqrt(distSq) || 1;

      if (pt.toolId === 'fan' && dist < TOOL_PHYSICS.fanRadius) {
        const f = TOOL_PHYSICS.fanForce * (TOOL_PHYSICS.fanRadius - dist) / TOOL_PHYSICS.fanRadius;
        if (bot.body) bot.setVelocityX(bot.body.velocity.x + f * 28);
        this.emitSpark(pt.x + (Math.random() - 0.5) * 20, pt.y - 8, 1);
      } else if (pt.toolId === 'magnet' && dist < TOOL_PHYSICS.magnetRadius && dist > 12) {
        const f = TOOL_PHYSICS.magnetForce * (1 - dist / TOOL_PHYSICS.magnetRadius);
        if (bot.body) bot.setVelocity(bot.body.velocity.x + (dx / dist) * f * 18, bot.body.velocity.y + (dy / dist) * f * 9);
      } else if (pt.toolId === 'balloon') {
        if (bot.body) bot.setVelocityY(bot.body.velocity.y + TOOL_PHYSICS.balloonForce * 22);
      } else if (pt.toolId === 'spring' && dist < 65) {
        if (bot.body && bot.body.velocity.y > -80) {
          bot.setVelocityY(bot.body.velocity.y * 0.6 + TOOL_PHYSICS.springImpulseY * 380);
          bot.setVelocityX(bot.body.velocity.x + TOOL_PHYSICS.springImpulseX * 160);
        }
      } else if (pt.toolId === 'rocket' && dist < 50) {
        if (bot.body) bot.setVelocityY(bot.body.velocity.y + TOOL_PHYSICS.rocketImpulse * 70);
      } else if (pt.toolId === 'bumper' && dist < 38) {
        const nx = dx / dist;
        if (bot.body) bot.setVelocityX(bot.body.velocity.x + nx * TOOL_PHYSICS.bumperImpulse * 140);
      } else if (pt.toolId === 'portal_gate' && dist < 42) {
        if (bot.body) bot.setVelocityX(bot.body.velocity.x + TOOL_PHYSICS.portalBoost * 220);
        this.emitSpark(pt.x, pt.y, 3);
      }
    }
  }

  private emitSpark(x: number, y: number, n = 1) {
    if (this.particles) this.particles.emitParticle(n, x, y);
  }

  private onRun() {
    if (this.isRunning && !this.isPaused) {
      this.checkRunEnd(true);
      return;
    }
    if (this.isPaused) {
      this.isPaused = false;
      if (this.bot && this.bot.body) this.bot.setDrag(0.1, 0.03);
      this.updateHudState();
      return;
    }

    this.isRunning = true;
    this.isPaused = false;
    this.runStartTime = this.time.now;
    this.runCount++;
    if (this.save.quick) this.save.quick.runCount = this.runCount;
    (this.analytics as any).setSimRunning?.(true);
    this.analytics.track('experiment_run', { run_index: this.runCount });

    if (this.bot) {
      this.bot.setVelocity(40, -30);
      this.applyInitialLaunch();
    }

    this.simTimer = this.time.addEvent({
      delay: 18,
      loop: true,
      callback: () => {
        if (this.isRunning && !this.isPaused && this.bot) {
          this.applyToolForces();
          this.checkAutoEnd();
        }
      },
    });

    this.audio.run();
    this.cameras.main.shake(130, 0.005);
    if (this.botTag) this.botTag.setText('WHEEE!').setColor('#ffd84a');
    this.updateHudState();
    this.hud.hideToast();

    this.time.delayedCall(3800, () => {
      if (this.isRunning && !this.isPaused) this.checkRunEnd(true);
    });
  }

  private applyInitialLaunch() {
    if (!this.bot || !this.bot.body) return;
    let vx = 95;
    let vy = -155;

    for (let i = 0; i < this.placedTools.length; i++) {
      const pt = this.placedTools[i];
      const d = Phaser.Math.Distance.Between(this.bot.x, this.bot.y, pt.x, pt.y);
      if (pt.toolId === 'spring' && d < 85) {
        vy += TOOL_PHYSICS.springImpulseY * 520;
        vx += TOOL_PHYSICS.springImpulseX * 280;
      }
      if (pt.toolId === 'ramp' && d < 100) {
        const rad = (pt.angle * Math.PI) / 180;
        vx += Math.cos(rad) * 145;
        vy += Math.sin(rad) * -95;
      }
      if (pt.toolId === 'rocket' && d < 60) {
        vy += TOOL_PHYSICS.rocketImpulse * 160;
      }
    }

    this.bot.setVelocity(vx, vy);
    this.emitSpark(this.bot.x, this.bot.y, 4);
  }

  private checkAutoEnd() {
    if (!this.bot || !this.bot.body) return;
    const v = this.bot.body.velocity;
    if (this.bot.x > this.goalX + 60 || this.bot.x < 30 || this.bot.y > worldFloorY(480) + 30 || (Math.abs(v.x) < 8 && Math.abs(v.y) < 12 && this.time.now - this.runStartTime > 1400)) {
      this.checkRunEnd();
    }
  }

  private checkRunEnd(forced = false) {
    if (!this.bot || !this.isRunning) return;
    const dist = measureDistance(this.bot.x, this.originX);
    const outcome = evaluateRun(this.bot.x, this.originX, this.goalX, this.bestDistance);
    this.lastRunDistance = outcome.distance;

    if (outcome.distance > this.bestDistance + 0.5) {
      this.bestDistance = outcome.distance;
      if (this.save.quick) this.save.quick.bestDistance = this.bestDistance;
    }

    (this.analytics as any).markPhysicsResult?.(outcome.distance, outcome.success ? 1 : 0);
    (this.analytics as any).markExperimentRun?.(outcome.distance, outcome.success);

    const curSig = toolSignature(this.placedTools.map((p) => ({ toolId: p.toolId, x: p.x, y: p.y, angle: p.angle })));
    const before = { rule: this.currentRule, toolSignature: this.lastToolSignature || curSig };
    const after = { rule: this.currentRule, toolSignature: curSig };
    const changeKind = detectVariableChange(before, after);
    const changed = changeKind !== 'none' || this.runCount > 1;

    if (shouldMarkSecondExperiment({ runIndexAfter: this.runCount, hasChangedVariable: changed, alreadyMarked: this.hasSecondStarted })) {
      this.hasSecondStarted = true;
      (this.analytics as any).markSecondExperiment?.();
      this.addDiscovery('Second Experiment', 'Modified a variable and ran again.');
      if (this.mode === 'quick' && this.save.quick) {
        this.save.quick.unlockedLowGravity = true;
        this.save.unlockedTools = [...new Set([...(this.save.unlockedTools || []), 'magnet', 'balloon'])];
      }
    }
    this.lastToolSignature = curSig;

    if (this.save.quick) this.save.quick.completedOnce = true;
    writeSave(this.save);

    this.stopSim();

    const title = outcome.success ? 'Passed the red line!' : 'Launch recorded';
    const body = `${Math.round(outcome.distance)} px  ${outcome.success ? '✓ Goal' : ''}`;
    const deltaStr = outcome.improved ? `+${Math.round(outcome.delta)} vs best` : '';
    const nextQ = this.runCount === 1 ? 'Change the rule or add/remove a part and run again.' : 'Beat your best distance with fewer tools?';

    this.hud.showResult({ title, body, delta: deltaStr, nextQuestion: nextQ });
    this.audio[outcome.success ? 'success' : 'fail']();

    this.updateHudState();
    if (outcome.success && this.runCount === 1) {
      this.addDiscovery('Red Line First Clear', `Distance ${Math.round(outcome.distance)} px.`);
    }
  }

  private addDiscovery(title: string, detail: string) {
    this.save = loadSave();
    const updated = addDiscovery(this.save, { title, detail, mode: this.mode, rule: this.currentRule });
    this.save = updated;
    this.updateHudState();
  }

  private stopSim() {
    this.isRunning = false;
    this.isPaused = false;
    (this.analytics as any).setSimRunning?.(false);
    if (this.simTimer) {
      this.simTimer.remove();
      this.simTimer = undefined;
    }
    if (this.bot) {
      this.bot.setDrag(80, 60);
    }
    this.updateHudState();
  }

  private onPause() {
    if (!this.isRunning) return;
    this.isPaused = !this.isPaused;
    if (this.bot) this.bot.setDrag(this.isPaused ? 200 : 0.1, this.isPaused ? 140 : 0.03);
    this.updateHudState();
  }

  private resetScene(initial = false) {
    this.stopSim();
    this.hasSecondStarted = false;

    this.placedTools.forEach((p) => p.sprite.destroy());
    this.placedTools = [];
    this.selectedPlaced = null;
    this.selectedToolId = null;

    this.createBot(this.botStartX, this.botStartY);

    if (this.mode === 'quick') {
      this.placeTool('ramp', 215, 330, 8);
      this.placeTool('spring', 145, 325, 0);
      if (this.runCount >= 1) {
        this.placeTool('fan', 260, 300, 0);
      }
      this.currentRule = 'normal';
      this.physics.world.gravity.y = getRule('normal').gravityY * 980;
      this.goalX = 520;
    } else {
      this.goalX = 610;
      if (!initial && this.save.freeLab && this.save.freeLab.tools.length) {
        this.restorePlacedTools(this.save.freeLab.tools);
      }
    }

    this.lastToolSignature = toolSignature(this.placedTools.map((p) => ({ toolId: p.toolId, x: p.x, y: p.y, angle: p.angle })));
    this.updateHudState();
    if (!initial) this.audio.click();
  }

  private restorePlacedTools(list: PlacedToolSave[]) {
    list.forEach((item) => {
      if (TOOLS[item.toolId]) {
        this.placeTool(item.toolId, item.x, item.y, item.angle || 0);
        const last = this.placedTools[this.placedTools.length - 1];
        if (last) last.angle = item.angle || 0;
      }
    });
  }

  private saveIfFree() {
    if (this.mode !== 'free') return;
    const tools: PlacedToolSave[] = this.placedTools.map((p) => ({
      toolId: p.toolId,
      x: Math.round(p.x),
      y: Math.round(p.y),
      angle: Math.round(p.angle),
    }));
    this.save.freeLab = { tools, rule: this.currentRule };
    writeSave(this.save);
  }

  private switchMode(m: GameMode) {
    if (m === this.mode) return;
    this.stopSim();
    this.mode = m;
    this.save.lastMode = m;
    writeSave(this.save);
    (this.analytics as any).setMode?.(m);
    this.analytics.track('mode_changed', { mode: m });
    this.resetScene();
    this.updateHudState();
  }

  private loadChallenge(c: any) {
    this.stopSim();
    this.mode = 'challenge';
    this.currentRule = c.rule || 'normal';
    this.physics.world.gravity.y = getRule(this.currentRule).gravityY * 980;
    (this.analytics as any).setMode?.('challenge');
    (this.analytics as any).setChallenge?.(c.id);
    (this.analytics as any).setRule?.(this.currentRule);

    this.resetScene();
    if (c.starterTools && Array.isArray(c.starterTools)) {
      c.starterTools.forEach((st: any) => this.placeTool(st.toolId, st.x || 170, st.y || 385, st.angle || 0));
    }
    if (c.targetX) this.goalX = c.targetX;
    this.updateHudState();
    this.hud.showResult({ title: c.title || 'Challenge', body: c.goal || '' });
  }

  private loadDaily() {
    const dkey = todayKey();
    const seed = dailySeed(dkey);
    const daily = dailyChallenge(seed);
    daily.id = `daily_${daily.id || 'x'}`;
    daily.title = `Daily: ${daily.title || 'Experiment'}`;
    this.loadChallenge(daily);
  }

  private toggleMute() {
    const m = !this.audio.isMuted();
    this.audio.setMuted(m);
    if (this.save.settings) this.save.settings.muted = m;
    writeSave(this.save);
    this.updateHudState();
  }

  private shareResult() {
    const enc = encodeSceneCode({
      tools: this.placedTools.map((p) => ({ toolId: p.toolId, x: p.x, y: p.y, angle: p.angle })),
      rule: this.currentRule,
      mode: this.mode,
    });
    if (enc && enc.length > 4) {
      try {
        navigator.clipboard.writeText(enc).then(() => {
          this.hud.showResult({ title: 'Code copied', body: 'Use Import in menu to load this layout.' });
        }).catch(() => { prompt('Scene code:', enc); });
      } catch { prompt('Scene code:', enc); }
      this.analytics.track('share_complete');
    }
  }

  private importCode(code: string) {
    const dec = decodeSceneCode(code);
    if (!dec || !dec.tools) {
      this.hud.showResult({ title: 'Bad code', body: 'Could not load.' });
      return;
    }
    this.stopSim();
    this.currentRule = dec.rule || 'normal';
    this.physics.world.gravity.y = getRule(this.currentRule).gravityY * 980;
    this.resetScene();
    this.restorePlacedTools(dec.tools);
    this.updateHudState();
    this.analytics.track('mode_changed');
  }

  private simAd() {
    (this.analytics as any).markAdExposed?.();
    this.hud.runSimAd(
      () => this.analytics.track('ad_offer'),
      () => this.analytics.track('ad_start'),
      () => {
        this.analytics.track('ad_complete');
        this.hud.showResult({ title: 'Back to lab', body: 'Ad simulation done.' });
      }
    );
  }

  private playLastReplay() {
    this.resetScene();
    setTimeout(() => this.onRun(), 80);
  }

  private undoLast() {
    if (!this.placedTools.length) return;
    const last = this.placedTools.pop()!;
    last.sprite.destroy();
    this.saveIfFree();
    this.analytics.track('undo');
    this.updateHudState();
  }

  update(_time: number, _delta: number) {
    if (this.bot && this.botTag) {
      this.botTag.setPosition(this.bot.x, this.bot.y - 50);
      if (!this.isRunning) this.botTag.setText('BUDDY').setColor('#ffffff');
    }
    if (this.debug && this.bot && this.hud) {
      const summary = (this.analytics as any).getSessionSummary?.() || {};
      const evCount = summary.event_count || 0;
      this.hud.setDebugText(`x:${this.bot.x.toFixed(0)} d:${measureDistance(this.bot.x, this.originX).toFixed(0)} r:${this.runCount} ev:${evCount}`);
    }
  }

  shutdown() {
    try { (this.analytics as any).destroy?.(); } catch {}
    try { this.hud?.destroy?.(); } catch {}
  }
}
